import React, { useState, useEffect } from 'react';
import { 
  Heart, Users, Zap, MapPin, Share2, CheckCircle, Sun,
  ArrowLeft, ArrowRight, Gift, Shield, Play, Camera,
  Battery, Lightbulb, CloudOff, Calendar, TrendingUp,
  AlertTriangle, Home, ExternalLink, Loader2
} from 'lucide-react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API = process.env.REACT_APP_BACKEND_URL;

const SocialProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [project, setProject] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadProject = async () => {
      try {
        const [projectRes, donationsRes] = await Promise.all([
          axios.get(`${API}/api/social-projects/public/${projectId}`),
          axios.get(`${API}/api/social-projects/public/${projectId}/donations?limit=20`)
        ]);
        setProject(projectRes.data);
        setDonations(donationsRes.data);
      } catch (error) {
        console.error('Error loading project:', error);
        // Usar proyecto de ejemplo si no existe en BD
        setProject(getDefaultProject(projectId));
      } finally {
        setLoading(false);
      }
    };
    
    loadProject();
  }, [projectId]);

  const getDefaultProject = (id) => ({
    id,
    title: 'Luz para Escuela Rural de Concepción',
    location: 'Loreto, Concepción',
    coordinates: '-23.4500, -57.2833',
    community_name: 'Escuela Básica N° 1847 "San Francisco de Asís"',
    description: 'La Escuela Básica N° 1847 no tiene acceso a electricidad confiable.',
    short_description: 'Sistema solar de 3kW para escuela con 47 niños.',
    story: `La Escuela Básica N° 1847 "San Francisco de Asís" está ubicada en la comunidad rural de Loreto, a 45 km de la ciudad de Concepción. 

Durante años, los 47 niños que asisten a esta escuela han tenido que estudiar bajo condiciones muy difíciles. Los cortes de luz son constantes, a veces durando días enteros. Cuando esto ocurre, los profesores deben recurrir a velas y mecheros para poder continuar con las clases.

La directora, María González, nos contó: "Tenemos computadoras que nos donaron hace 2 años, pero casi nunca las podemos usar porque no hay luz. Los niños se emocionan cuando pueden encenderlas, pero es muy poco frecuente."

El sistema de energía solar que queremos instalar cambiará completamente esta realidad. Con 3kW de potencia y baterías de respaldo, la escuela tendrá energía confiable las 24 horas, permitiendo que los niños usen las computadoras, el proyector, y estudien con luz adecuada.`,
    current_situation: 'La escuela sufre cortes de luz casi diarios. Los niños estudian con velas y no pueden usar las computadoras donadas. Las clases nocturnas de adultos fueron suspendidas por falta de iluminación.',
    image_url: 'https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=800',
    gallery_images: [
      'https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=800',
      'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800',
      'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=800'
    ],
    video_url: null,
    goal_amount: 45000000,
    raised_amount: 0,
    donors_count: 0,
    category: 'Educación',
    status: 'active',
    beneficiaries: 47,
    beneficiaries_description: '47 niños y 5 profesores',
    system_size: '3 kW',
    panels_count: 7,
    panel_watts: 450,
    batteries_count: 4,
    inverter_size: '3.5 kW',
    estimated_monthly_generation: 450,
    impact: 'Luz para 47 niños y 5 profesores, acceso a computadoras y proyector',
    co2_offset_yearly: 3600,
    hours_of_light_daily: 12,
    devices_powered: '10 computadoras, 1 proyector, 20 luces LED, 2 ventiladores',
    power_outages_monthly: 20,
    has_electricity: true,
    updates: [],
    testimonials: [
      { name: 'María González', role: 'Directora', text: 'Los niños merecen poder estudiar con luz. Este proyecto cambiará sus vidas.' },
      { name: 'Juan Ramírez', role: 'Profesor', text: 'Tenemos tantas ganas de poder usar las computadoras con los chicos.' }
    ],
    milestones: [
      { amount: 10000000, title: 'Primer hito', description: 'Compramos los paneles solares', reached: false },
      { amount: 25000000, title: 'Segundo hito', description: 'Compramos el inversor y baterías', reached: false },
      { amount: 45000000, title: 'Meta completa', description: '¡Instalación del sistema!', reached: false }
    ]
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateProgress = (raised, goal) => {
    return Math.min((raised / goal) * 100, 100);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays < 1) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    return `Hace ${diffDays} días`;
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Ayudá a: ${project?.title}`,
        text: project?.short_description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('¡Link copiado al portapapeles!');
    }
  };

  const predefinedAmounts = [50000, 100000, 250000, 500000, 1000000, 2000000];

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-zinc-900 mb-4">Proyecto no encontrado</h2>
          <Link to="/proyectos/accion-social" className="text-emerald-600 hover:underline">
            Volver a proyectos
          </Link>
        </div>
      </div>
    );
  }

  const allImages = project.gallery_images?.length > 0 
    ? project.gallery_images 
    : [project.image_url];

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Image */}
      <section className="relative pt-16 h-[50vh] min-h-[400px]">
        <img 
          src={allImages[activeImageIndex]} 
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        {/* Back button */}
        <Link 
          to="/proyectos/accion-social"
          className="absolute top-20 left-4 md:left-8 flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Volver</span>
        </Link>

        {/* Image navigation */}
        {allImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {allImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeImageIndex ? 'bg-white w-6' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Video button */}
        {project.video_url && (
          <button
            onClick={() => setShowVideo(true)}
            className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 bg-red-600 rounded-full text-white hover:bg-red-700 transition-colors"
          >
            <Play className="w-4 h-4" />
            <span className="text-sm font-medium">Ver Video</span>
          </button>
        )}

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-7xl mx-auto">
            <span className="inline-block px-3 py-1 bg-emerald-500 rounded-full text-sm font-medium text-white mb-3">
              {project.category}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
              {project.title}
            </h1>
            <div className="flex items-center gap-2 text-white/80">
              <MapPin className="w-4 h-4" />
              <span>{project.location}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Progress Card */}
              <div className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-3xl font-bold text-emerald-600">
                      Gs. {formatCurrency(project.raised_amount)}
                    </p>
                    <p className="text-zinc-500">
                      recaudados de Gs. {formatCurrency(project.goal_amount)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-zinc-900">{project.donors_count}</p>
                    <p className="text-zinc-500">donantes</p>
                  </div>
                </div>
                
                <div className="h-4 bg-zinc-100 rounded-full overflow-hidden mb-4">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                    style={{ width: `${calculateProgress(project.raised_amount, project.goal_amount)}%` }}
                  ></div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDonationModal(true)}
                    className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <Heart className="w-5 h-5" />
                    Donar Ahora
                  </button>
                  <button
                    onClick={handleShare}
                    className="px-4 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Current Situation Alert */}
              {project.current_situation && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <AlertTriangle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-amber-800 mb-2">Situación Actual</h3>
                      <p className="text-amber-700">{project.current_situation}</p>
                      {project.power_outages_monthly && (
                        <p className="text-amber-600 text-sm mt-2">
                          ⚡ Aproximadamente {project.power_outages_monthly} cortes de luz por mes
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Story */}
              <div className="bg-white rounded-2xl p-6 border border-zinc-200">
                <h2 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5 text-emerald-500" />
                  Conocé a la Comunidad
                </h2>
                {project.community_name && (
                  <p className="text-emerald-600 font-medium mb-4">{project.community_name}</p>
                )}
                <div className="prose prose-zinc max-w-none">
                  {(project.story || project.description).split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-zinc-600 mb-4 leading-relaxed">{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Testimonials */}
              {project.testimonials?.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border border-zinc-200">
                  <h2 className="text-xl font-bold text-zinc-900 mb-4">Voces de la Comunidad</h2>
                  <div className="space-y-4">
                    {project.testimonials.map((testimonial, index) => (
                      <div key={index} className="bg-zinc-50 rounded-xl p-4">
                        <p className="text-zinc-600 italic mb-3">"{testimonial.text}"</p>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                            <span className="text-emerald-600 font-bold text-sm">
                              {testimonial.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900 text-sm">{testimonial.name}</p>
                            <p className="text-zinc-500 text-xs">{testimonial.role}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Updates */}
              {project.updates?.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border border-zinc-200">
                  <h2 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-500" />
                    Actualizaciones del Proyecto
                  </h2>
                  <div className="space-y-4">
                    {project.updates.map((update, index) => (
                      <div key={index} className="flex gap-4 pb-4 border-b border-zinc-100 last:border-0">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                        <div>
                          <p className="text-zinc-600">{update.text}</p>
                          <p className="text-zinc-400 text-sm mt-1">{update.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              
              {/* System Specs */}
              <div className="bg-white rounded-2xl p-6 border border-zinc-200">
                <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
                  <Sun className="w-5 h-5 text-yellow-500" />
                  Sistema Solar a Instalar
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-zinc-100">
                    <span className="text-zinc-500">Capacidad total</span>
                    <span className="font-bold text-zinc-900">{project.system_size}</span>
                  </div>
                  {project.panels_count && (
                    <div className="flex justify-between py-2 border-b border-zinc-100">
                      <span className="text-zinc-500">Paneles solares</span>
                      <span className="font-bold text-zinc-900">
                        {project.panels_count} x {project.panel_watts || 450}W
                      </span>
                    </div>
                  )}
                  {project.batteries_count && (
                    <div className="flex justify-between py-2 border-b border-zinc-100">
                      <span className="text-zinc-500">Baterías</span>
                      <span className="font-bold text-zinc-900">{project.batteries_count} unidades</span>
                    </div>
                  )}
                  {project.inverter_size && (
                    <div className="flex justify-between py-2 border-b border-zinc-100">
                      <span className="text-zinc-500">Inversor</span>
                      <span className="font-bold text-zinc-900">{project.inverter_size}</span>
                    </div>
                  )}
                  {project.estimated_monthly_generation && (
                    <div className="flex justify-between py-2">
                      <span className="text-zinc-500">Generación mensual</span>
                      <span className="font-bold text-emerald-600">~{project.estimated_monthly_generation} kWh</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Impact */}
              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
                <h3 className="text-lg font-bold text-emerald-800 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Impacto del Proyecto
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="font-bold text-emerald-800">{project.beneficiaries} beneficiarios</p>
                      {project.beneficiaries_description && (
                        <p className="text-emerald-600 text-sm">{project.beneficiaries_description}</p>
                      )}
                    </div>
                  </div>
                  {project.hours_of_light_daily && (
                    <div className="flex items-center gap-3">
                      <Lightbulb className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-bold text-emerald-800">{project.hours_of_light_daily} horas de luz/día</p>
                        <p className="text-emerald-600 text-sm">Energía confiable garantizada</p>
                      </div>
                    </div>
                  )}
                  {project.co2_offset_yearly && (
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-bold text-emerald-800">{formatCurrency(project.co2_offset_yearly)} kg CO₂/año</p>
                        <p className="text-emerald-600 text-sm">Reducción de emisiones</p>
                      </div>
                    </div>
                  )}
                </div>
                {project.devices_powered && (
                  <div className="mt-4 pt-4 border-t border-emerald-200">
                    <p className="text-emerald-700 text-sm">
                      <strong>Equipos a alimentar:</strong> {project.devices_powered}
                    </p>
                  </div>
                )}
              </div>

              {/* Milestones */}
              {project.milestones?.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border border-zinc-200">
                  <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    Hitos del Proyecto
                  </h3>
                  <div className="space-y-4">
                    {project.milestones.map((milestone, index) => {
                      const reached = project.raised_amount >= milestone.amount;
                      return (
                        <div key={index} className={`p-3 rounded-xl ${reached ? 'bg-emerald-50 border border-emerald-200' : 'bg-zinc-50'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`font-medium ${reached ? 'text-emerald-700' : 'text-zinc-700'}`}>
                              {milestone.title}
                            </span>
                            {reached && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                          </div>
                          <p className="text-sm text-zinc-500">{milestone.description}</p>
                          <p className={`text-sm font-bold mt-1 ${reached ? 'text-emerald-600' : 'text-zinc-400'}`}>
                            Gs. {formatCurrency(milestone.amount)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent Donations */}
              <div className="bg-white rounded-2xl p-6 border border-zinc-200">
                <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Donaciones Recientes
                </h3>
                {donations.length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {donations.map((donation, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-zinc-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                            <Heart className="w-4 h-4 text-emerald-500" />
                          </div>
                          <span className="text-sm text-zinc-700">{donation.donor_name}</span>
                        </div>
                        <span className="text-sm font-bold text-emerald-600">
                          Gs. {formatCurrency(donation.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Heart className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
                    <p className="text-zinc-500 text-sm">Aún no hay donaciones</p>
                    <p className="text-zinc-400 text-xs">¡Sé el primero!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Modal */}
      {showDonationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-zinc-900">Hacer una Donación</h3>
                <button 
                  onClick={() => setShowDonationModal(false)}
                  className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  <span className="text-2xl text-zinc-500">&times;</span>
                </button>
              </div>
              
              <div className="bg-zinc-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-zinc-600 mb-1">Donando a:</p>
                <p className="font-bold text-zinc-900">{project.title}</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-zinc-700 mb-3">
                  Seleccioná un monto o ingresá el tuyo
                </label>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {predefinedAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setDonationAmount(amount.toString())}
                      className={`py-3 rounded-xl text-sm font-medium transition-colors ${
                        donationAmount === amount.toString()
                          ? 'bg-emerald-500 text-white'
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      Gs. {formatCurrency(amount)}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">Gs.</span>
                  <input
                    type="number"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(e.target.value)}
                    placeholder="Otro monto"
                    className="w-full pl-12 pr-4 py-3 border border-zinc-300 rounded-xl focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-emerald-800">
                  <strong>💚 Tu donación ayudará a:</strong> {project.beneficiaries} personas 
                  de {project.location} a tener acceso a energía limpia.
                </p>
              </div>

              <a
                href={`https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20donar%20Gs.%20${formatCurrency(parseInt(donationAmount) || 0)}%20al%20proyecto:%20${encodeURIComponent(project.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Heart className="w-5 h-5" />
                Continuar con la Donación
              </a>
              
              <p className="text-xs text-zinc-500 text-center mt-4">
                Te contactaremos por WhatsApp para coordinar el pago de forma segura.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideo && project.video_url && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div className="max-w-4xl w-full aspect-video">
            <iframe
              src={project.video_url}
              className="w-full h-full rounded-xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default SocialProjectDetailPage;
